import type { FileHit } from "../../api/types";

/**
 * What kind of thing an extension is, which is what the card colours itself by.
 *
 * By family rather than by extension: a list of results is scanned, not read, and one hue per
 * kind is what makes "the spreadsheets" findable at a glance. Anything unlisted falls to `other`
 * and stays grey — including the formats we index for their metadata but cannot read.
 */
const FAMILIES: Record<string, string> = {
  pdf: "pdf",
  doc: "doc",
  docx: "doc",
  rtf: "doc",
  odt: "doc",
  txt: "doc",
  md: "doc",
  xls: "sheet",
  xlsx: "sheet",
  xlsm: "sheet",
  csv: "sheet",
  ppt: "slide",
  pptx: "slide",
  pptm: "slide",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  bmp: "image",
  webp: "image",
  heic: "image",
  svg: "image",
  mp4: "media",
  mov: "media",
  avi: "media",
  wmv: "media",
  mp3: "media",
  wav: "media",
  m4a: "media",
};

/**
 * One matched file.
 *
 * Every value shown comes from the `search_files` payload — the same structured result the model
 * reasoned over — so a card cannot claim anything the corpus did not return. The title is the only
 * way to open the file: it links to the source's own web URL, so the click is authenticated by
 * SharePoint or Drive rather than proxied by us.
 */
export function FileCard({ hit }: { hit: FileHit }): JSX.Element {
  const extension = (hit.extension || "").replace(".", "").toLowerCase();
  return (
    <article className={`file-card file-card--${FAMILIES[extension] ?? "other"}`}>
      <div className="file-card__type" aria-hidden="true">
        {extension.toUpperCase() || "FILE"}
      </div>
      <div className="file-card__body">
        <div className="file-card__heading">
          {hit.web_url ? (
            <a
              className="file-card__title"
              href={hit.web_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {hit.name}
            </a>
          ) : (
            <span className="file-card__title">{hit.name}</span>
          )}
          <span className={`file-card__score ${confidenceTone(hit.score)}`}>
            {Math.round(hit.score * 100)}%
          </span>
        </div>
        <div className="file-card__meta">
          {hit.source && <span>{hit.source}</span>}
          {hit.last_modified && <span>{formatDate(hit.last_modified)}</span>}
          {hit.size > 0 && <span>{formatSize(hit.size)}</span>}
          {hit.why_matched && <span className="file-card__why">{hit.why_matched}</span>}
        </div>
        {hit.summary && (
          <p className="file-card__summary">
            {hit.summary}
            {hit.summary_truncated && <span className="file-card__ellipsis">…</span>}
          </p>
        )}
        {hit.other_versions_suppressed > 0 && (
          <p className="file-card__versions">
            {hit.other_versions_suppressed} older{" "}
            {hit.other_versions_suppressed === 1 ? "version" : "versions"} of this document were
            collapsed into this result.
          </p>
        )}
      </div>
    </article>
  );
}

/** Three bands rather than a raw number: a score is a ranking signal, not a measurement. */
function confidenceTone(score: number): string {
  if (score >= 0.7) {
    return "file-card__score--high";
  }
  return score >= 0.4 ? "file-card__score--medium" : "file-card__score--low";
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function formatSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size < 10 && unit > 0 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`;
}
